import type { PublicParticipant } from '../types/public'

export type PublicGameType = 'football' | 'formula1'

function normalizeGameType(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

function isLeft(participant: PublicParticipant | null | undefined): boolean {
  return (participant?.publicVisibility ?? 'active') === 'left'
}

export function asPublicEnrollmentList(data: unknown): PublicParticipant[] {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    const inner = (data as { data?: unknown; enrollments?: unknown }).data
      ?? (data as { enrollments?: unknown }).enrollments
    if (Array.isArray(inner)) return inner
  }
  return []
}

/** Active World Football leagues for a participant. */
export function getFootballTournamentIdsFromParticipant(
  participant: PublicParticipant | null | undefined,
): string[] {
  if (!participant || isLeft(participant)) return []

  const ids = [
    participant.publicTournamentId,
    ...(participant.participantTournaments ?? []).map((pt) => pt.publicTournamentId),
    ...(participant.favoriteTournamentIds ?? []),
  ].filter((id): id is string => Boolean(id))
  return Array.from(new Set(ids))
}

export function isActivelyEnrolledInFootball(participant: PublicParticipant | null | undefined): boolean {
  if (!participant || isLeft(participant)) return false
  if (normalizeGameType(participant.gameType ?? participant.publicTournament?.gameType) === 'football') {
    return true
  }
  return (participant.participantTournaments ?? []).some(
    (pt) => normalizeGameType(pt.publicTournament?.gameType) === 'football',
  )
}

export function findActiveFootballParticipant(
  enrollments: PublicParticipant[],
): PublicParticipant | undefined {
  return asPublicEnrollmentList(enrollments).find((e) => isActivelyEnrolledInFootball(e))
}

export function isFormula1Participant(participant: PublicParticipant | null | undefined): boolean {
  if (!participant) return false
  if (normalizeGameType(participant.gameType) === 'formula1') return true
  if (normalizeGameType(participant.publicTournament?.gameType) === 'formula1') return true
  return (participant.participantTournaments ?? []).some(
    (pt) => normalizeGameType(pt.publicTournament?.gameType) === 'formula1',
  )
}

export function findActiveFormula1Participant(
  enrollments: PublicParticipant[],
): PublicParticipant | undefined {
  return asPublicEnrollmentList(enrollments).find((e) => !isLeft(e) && isFormula1Participant(e))
}

export function findPublicLocationFromEnrollments(
  enrollments: PublicParticipant[],
): { countryCode: string; city: string } | null {
  const withLocation = asPublicEnrollmentList(enrollments).find(
    (e) => !isLeft(e) && e.countryCode?.trim() && e.city?.trim(),
  )
  if (!withLocation) return null
  return { countryCode: withLocation.countryCode.trim().toUpperCase(), city: withLocation.city!.trim() }
}

export function upsertPublicEnrollment(
  enrollments: PublicParticipant[] | undefined,
  patch: {
    gameType: PublicGameType
    tournamentIds: string[]
    countryCode: string
    city: string
  },
): PublicParticipant[] {
  const list = asPublicEnrollmentList(enrollments)
  const tournamentIds = Array.from(new Set(patch.tournamentIds.filter(Boolean)))
  const idx = list.findIndex((e) => {
    if (patch.gameType === 'formula1') return isFormula1Participant(e)
    return isActivelyEnrolledInFootball(e) || normalizeGameType(e.gameType) === 'football'
  })
  const current = idx >= 0 ? list[idx] : undefined
  const next: PublicParticipant = {
    id: current?.id || `optimistic-${patch.gameType}`,
    gameType: patch.gameType,
    publicTournamentId: tournamentIds[0] ?? current?.publicTournamentId ?? null,
    userId: current?.userId || '',
    countryCode: patch.countryCode,
    countryName: current?.countryName || '',
    continentCode: current?.continentCode || '',
    city: patch.city,
    joinedAt: current?.joinedAt || new Date().toISOString(),
    publicVisibility: 'active',
    leftPublicAt: null,
    favoriteTournamentIds: tournamentIds,
    participantTournaments: tournamentIds.map((id) => ({
      publicParticipantId: current?.id || '',
      publicTournamentId: id,
    })),
  }
  if (idx >= 0) {
    const copy = [...list]
    copy[idx] = { ...current, ...next, id: current!.id, userId: current!.userId }
    return copy
  }
  return [...list, next]
}
